import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { QrCode } from '../../platform/qr/QrCode'
import { useFocusRequest } from '../../platform/a11y/useFocusRequest'
import { useAnnouncement } from '../../platform/a11y/useAnnouncement'
import { emit } from '../../platform/analytics/analytics'
import { REQUIRES_RECENT_LOGIN, authErrorFor } from '../../auth/errors'
import { AuthFailure, type TotpEnrolment } from '../../auth/port'
import { useAuth } from '../../auth/useAuth'
import type { FieldError } from '../../auth/errors'
import { FormError } from '../../auth/FormError'
import './enrol.css'

/**
 * A base32 secret in groups of four.
 *
 * Someone typing 32 unbroken characters into a phone loses their place, and the
 * grouping is the whole reason a manual-entry path is usable at all. Spaces are
 * ignored by every authenticator app — the secret itself is unchanged.
 */
export function groupSecret(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? [secret]).join(' ')
}

type Props = {
  /** Called once the factor is on the account. */
  onDone: () => void
  onCancel: () => void
  /**
   * Firebase refused for want of a recent login. The host re-authenticates and
   * calls `retry`, which repeats the step that was refused. Absent, the mapped
   * sentence is shown and the user is on their own, as before.
   */
  onNeedsReauth?: (retry: () => void) => void
}


/**
 * Adding an authenticator app.
 *
 * **The secret is generated before the user proves anything**, which is how
 * TOTP works: they have to store it before they can produce a code from it.
 * Nothing reaches the account until `complete` succeeds, so cancelling is
 * simply abandoning this component — there is no half-enrolled state to undo.
 *
 * **The QR and the typeable key are the same string.** The key is not a
 * fallback for a broken QR so much as the path for anyone whose authenticator
 * is on the same device they are reading this on, where there is no second
 * camera to point at the screen.
 */
export function AddTotp({ onDone, onCancel, onNeedsReauth }: Props) {
  const auth = useAuth()
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null)
  const [error, setError] = useState<FieldError | null>(null)
  const [busy, setBusy] = useState(false)

  const codeRef = useRef<HTMLInputElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const focus = useFocusRequest({ code: codeRef, cancel: cancelRef })

  /**
   * What the live region says.
   *
   * Held in state and mounted from the FIRST render. The earlier version put
   * "Preparing…" in a region that the commit rendering the QR then destroyed —
   * so the one event worth hearing, that the code is ready, had no region left
   * to announce it. `MfaChallenge` paid for that same shape twice.
   */
  const [announcement, announce] = useAnnouncement()

  /**
   * Ask for a secret. Run on mount and again as the retry after a
   * re-authentication — hence a function, not the effect's body.
   */
  const begin = useCallback(() => {
    setError(null)
    void auth
      .beginTotpEnrolment()
      .then((ready) => {
        setEnrolment(ready)
        announce('Your setup code is ready. Scan the QR code, or type the key below it.')
        // The button that opened this panel was unmounted with it, so focus was
        // on <body> — measured in Chrome. jsdom cannot see it: it neither blurs
        // removed nodes nor disabled ones.
        focus('code')
      })
      .catch((thrown: unknown) => {
        const failure = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
        setError(failure)
        focus('cancel')
        if (failure.code === REQUIRES_RECENT_LOGIN) onNeedsReauth?.(begin)
      })
  }, [auth, focus, announce, onNeedsReauth])

  /**
   * Started once, on mount, and guarded by a ref.
   *
   * `generateSecret` is a network call that mints a new secret every time. In
   * `<StrictMode>` the effect runs twice in development, and without this the
   * QR on screen would encode the FIRST secret while the component held the
   * second — a code that verifies against nothing, in development only.
   */
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    // The button that opened this panel is gone. Cancel is the only control
    // here until the secret arrives, and `<body>` is not a place to leave a
    // keyboard user for the length of a network call. The QR-ready handler
    // moves focus on to the code box a moment later.
    //
    // **Why this one and not the other two in-flight waits.** web-tester noted
    // focus also parks on `<body>` mid-send in `AddPhone` and mid-removal in
    // `Security`, and asked whether the treatment should match. It should not,
    // and the distinguishing property is recovery: those two land somewhere
    // deliberate when the call returns, whereas this panel had NOTHING to
    // recover it — the opening click was the last focus event, and a user who
    // never saw the QR arrive stayed at the top of the document. A hop that
    // only shortens an already-recovering wait costs a second focus move and
    // can interrupt a pending announcement, which is worse than the wait.
    focus('cancel')
    emit('mfa_setup_started', { factor_type: 'totp' })
    begin()
    // `announce` joins the list rather than being left out of it — issue 218.
    // It costs nothing now that `useAnnouncement` returns a `useCallback`: the
    // identity is stable for the component's lifetime, so this effect still runs
    // once, and the `started` ref above still guards it either way.
  }, [begin, focus])

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (enrolment === null) return
    void submitCode(enrolment, String(new FormData(event.currentTarget).get('code') ?? ''))
  }

  async function submitCode(enrolment: TotpEnrolment, code: string) {
    setError(null)
    setBusy(true)
    try {
      await enrolment.complete(code, 'Authenticator app')
      emit('mfa_setup_completed', { factor_type: 'totp' })
      onDone()
    } catch (thrown) {
      const failure = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      setError(failure)
      // The retry starts over with a fresh secret rather than resubmitting.
      // The code is one-time and will have expired while the user
      // re-authenticated, and the session the secret was minted under predates
      // the re-authentication. Nothing is held for it.
      if (failure.code === REQUIRES_RECENT_LOGIN) {
        onNeedsReauth?.(() => {
          setEnrolment(null)
          begin()
        })
      }
      // Back to the box they have to correct. Verify disabled itself, and a
      // browser moves focus off a disabled control.
      focus('code')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="enrol">
      {/* One region, mounted from the first render and filled later — never
          inserted with its content already in it. The SAME element survives the
          loading → ready transition, which is what makes the change an update a
          screen reader announces rather than an insertion it may miss. */}
      <p className="enrol__note enrol__status" role="status">
        {announcement}
      </p>

      {enrolment === null ? (
        <>
          {error === null && <p className="enrol__note">Preparing…</p>}
          <FormError error={error} />
          {/* A way out even when setup never arrived — otherwise a failed
              `generateSecret` leaves a panel with nothing on it. */}
          <Button variant="outline" size="page" ref={cancelRef} onClick={onCancel}>
            Cancel
          </Button>
        </>
      ) : (
        <>
          {/* `<h2>`: this sits under an `<h1>` at first run and under Settings'
              `<h2>`… which would make it an h3 there. One level cannot be right
              for both hosts, and skipping h1 → h3 is the worse of the two — a
              screen-reader user navigating by heading hits a gap. Settings'
              section title is the h2 it nests under there. */}
          <h2 className="enrol__step">Scan this with your authenticator app</h2>
          <QrCode value={enrolment.uri} label="QR code for setting up your authenticator app" />

          <p className="enrol__note">
            Can&apos;t scan it? Enter this key instead:
            {/* `<code>` because it is a literal to be transcribed, and the class
                keeps the groups from being broken across lines mid-group. */}
            <code className="enrol__secret">{groupSecret(enrolment.secret)}</code>
          </p>

          <form className="enrol__form" onSubmit={onSubmit} noValidate>
            <Field
              label="Enter the 6-digit code from the app"
              name="code"
              autoComplete="one-time-code"
              inputMode="numeric"
              ref={codeRef}
              error={error?.field === 'code' ? error.message : undefined}
            />
            <FormError error={error} />
            <Button type="submit" variant="primary" size="page" disabled={busy}>
              {busy ? 'Verifying…' : 'Turn on'}
            </Button>
            <Button type="button" variant="outline" size="page" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
          </form>
        </>
      )}
    </div>
  )
}
